import { useMatch } from "react-router-dom";

import { useQueryParam } from "@/hooks/useQueryParam";
import { portalRoutes } from "@/router/routes/portalRoutes.ts";

/**
 * The interaction flags that are derived purely from the current route and the
 * `mode` query param — i.e. which management sub-flow the user has open. The
 * editing flag (`isEditingSimDetails`) is intentionally absent: it lives in a
 * Jotai atom, not in the URL.
 */
export interface DeviceRouteState {
  /** The `mode` query param, e.g. `"edit"`, `"booking"`, `"checkout"`. */
  mode: string | undefined;
  /** The wildcard part of the management route (e.g. `"subscription/display"`). */
  managementSubPath: string | undefined;
  isChangingVoiceSettings: boolean;
  isBookingRoaming: boolean;
  isChangingTariff: boolean;
  isModifyingWatchSim: boolean;
  isBookingWatchSIM: boolean;
}

/** Sub-path of the subscription-change rate-selection step. */
export const SUBSCRIPTION_CHANGE_SUBPATH = "subscription/change";
/** Sub-path of the subscription-change checkout step. */
export const SUBSCRIPTION_CHANGE_CHECKOUT_SUBPATH =
  "subscription/change/checkout";

/** Sub-paths that make up the full subscription-change flow (standalone layout). */
export const SUBSCRIPTION_CHANGE_FLOW_SUBPATHS = [
  SUBSCRIPTION_CHANGE_SUBPATH,
  SUBSCRIPTION_CHANGE_CHECKOUT_SUBPATH,
];

/** Sub-paths under the management route that count as an active subscription flow. */
const SUBSCRIPTION_FLOW_SUBPATHS = [
  ...SUBSCRIPTION_CHANGE_FLOW_SUBPATHS,
  "subscription/checkout",
  "subscription/automatic-renewal",
];
/**
 * Sub-path of the Watch SIM checkout step. Exported so
 * DeviceManagement.tsx's standalone-layout check can reuse it instead of
 * retyping the same route string in a second list.
 */
export const WATCH_SIM_CHECKOUT_SUBPATH = "watch-sim/checkout";

/** Sub-paths that count as an active Watch SIM modification flow. */
export const WATCH_SIM_FLOW_SUBPATHS = [
  WATCH_SIM_CHECKOUT_SUBPATH,
  "watch-sim/automatic-renewal",
];

/** Mobile pause/reactivate screens — dedicated routes with pinned edit cards. */
export const MOBILE_STANDALONE_EDIT_SUBPATHS = [
  "subscription/automatic-renewal",
  "watch-sim/automatic-renewal",
] as const;

/**
 * Resolves which device-management sub-flow is currently open from the route
 * and the `mode` query param.
 *
 * TODO Refactor routing matching logic, this is prone to break — the sub-flows
 * are matched by comparing the wildcard path segment against hard-coded lists.
 */
export const useDeviceRouteState = (): DeviceRouteState => {
  const [mode] = useQueryParam("mode");
  const managementUrlMatch = useMatch(`${portalRoutes.manageDevice.base}/*`);
  const roamingMatch = useMatch(`${portalRoutes.manageDevice.roaming.base}/*`);
  const watchSimMatch = useMatch(
    `${portalRoutes.manageDevice.watchSim.base}/*`,
  );

  const managementSubPath = managementUrlMatch?.params["*"];
  const isBooking = mode === "booking" || mode === "checkout";

  return {
    mode,
    managementSubPath,
    isChangingVoiceSettings: mode === "edit",
    isBookingRoaming: !!roamingMatch && isBooking,
    isBookingWatchSIM: !!watchSimMatch && isBooking,
    isChangingTariff:
      !!managementSubPath &&
      SUBSCRIPTION_FLOW_SUBPATHS.includes(managementSubPath),
    isModifyingWatchSim:
      !!managementSubPath &&
      WATCH_SIM_FLOW_SUBPATHS.includes(managementSubPath),
  };
};
